import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import styles from './childPlanning.module.scss'
import { CalendarApi, DateSelectArg, EventClickArg, EventContentArg, EventSourceInput } from '@fullcalendar/core'
import moment, { Moment } from 'moment-timezone'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { Daycare } from '@/types/Daycare'
import { EventCalendar } from '@/types/Calendar'
import allLocales from '@fullcalendar/core/locales-all'
import { getLocalFromLanguage, getTextFromLanguage } from '@/modules/helper/languageHelper'
import { renderEventContent } from '../../../../../planning/utils/calendarEvents'
import { CalendarView } from '../../../../../planning/utils/calendarParameters'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'
import { CalendarIcon } from '@mui/x-date-pickers'
import DatePickerDialog from '../../../../../planning/Dialog/datePicker/datePickerDialog'
import { Locale } from 'date-fns/locale'
import { format, subDays } from 'date-fns'
import Desktop from '@/modules/desktopMobile/desktop.tsx'

interface ChildPlanningProps {
    fetchData: (fromDate: Moment, toDate: Moment) => void
    calendarEvents: EventCalendar[]
    currentDaycare: Daycare
    hiddenDays: number[]
    handleSelect: (info: DateSelectArg) => void
    handleEventClick: (info: EventClickArg) => void
}

const ChildPlanning: FunctionComponent<ChildPlanningProps> = ({
    fetchData,
    calendarEvents,
    currentDaycare,
    hiddenDays,
    handleSelect,
    handleEventClick
}: ChildPlanningProps) => {
    const calendarRef: RefObject<FullCalendar | null> = useRef(null)

    const [isDatePickerDialogOpen, setDatePickerDialogOpen] = useState(false)

    const [calendarTitle, setCalendarTitle] = useState(moment().format('MMMM YYYY'))

    const [selectedDate, setSelectedDate] = useState<Moment>(moment())

    const [events, setEvents] = useState<EventSourceInput>()

    function handlePrev(): void {
        if (calendarRef.current) {
            calendarRef.current.getApi().prev()
            setNewCalendarTitle()
        }
    }

    function handleNext(): void {
        if (calendarRef.current) {
            calendarRef.current.getApi().next()
            setNewCalendarTitle()
        }
    }

    function setNewCalendarTitle(): void {
        if (!calendarRef.current) return

        const start: Date = calendarRef.current.getApi().view.activeStart
        const end: Date = subDays(calendarRef.current.getApi().view.activeEnd, 1)

        fetchData(moment(start), moment(end))

        const locale: Locale = getLocalFromLanguage()

        const newTitle: string = format(start, 'MMMM yyyy', { locale })

        if (calendarTitle !== newTitle) {
            setCalendarTitle(newTitle)
        }
    }

    useEffect(() => {
        setEvents(
            calendarEvents.map((event: EventCalendar) => {
                return {
                    ...event,
                    className: styles.EventCell
                }
            })
        )
    }, [calendarEvents])

    useEffect(() => {
        if (calendarRef.current) {
            const calendarApi: CalendarApi = calendarRef.current.getApi()
            if (calendarApi) {
                calendarApi.gotoDate(selectedDate.toDate())
                setNewCalendarTitle()
            }
        }
    }, [selectedDate])

    useEffect(() => {
        if (calendarRef.current) {
            const start: Moment = moment(calendarRef.current.getApi().view.activeStart)
            const end: Moment = moment(calendarRef.current.getApi().view.activeEnd).subtract(1, 'd')

            fetchData(start, end)
        }
    }, [currentDaycare])

    useEffect(() => {
        moment.locale(getTextFromLanguage())
    }, [])

    moment.locale(getTextFromLanguage())

    return (
        <div className={styles.CalendarHandler}>
            <div className={styles.CalendarAndHeader}>
                <div className={styles.Header}>
                    <ButtonTextIcon
                        type={'custom'}
                        onClick={handlePrev}
                        customText={undefined}
                        customIcon={<ArrowBack />}
                        className={styles.ButtonArrow}
                    />
                    <div className={styles.HeaderTitle} onClick={() => setDatePickerDialogOpen(true)}>
                        <p className={styles.Title}>{calendarTitle}</p>
                        <Desktop>
                            <ButtonIcon
                                icon={<CalendarIcon />}
                                onClick={() => setDatePickerDialogOpen(true)}
                                className={styles.Icon}
                            />
                        </Desktop>
                    </div>
                    <ButtonTextIcon
                        type={'custom'}
                        onClick={handleNext}
                        customText={undefined}
                        customIcon={<ArrowForward />}
                        className={styles.ButtonArrow}
                    />
                </div>
                <div className={styles.Calendar}>
                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        headerToolbar={false}
                        selectable={true}
                        height={'auto'}
                        initialView={'customOneMonth'}
                        views={{
                            customOneMonth: {
                                type: 'dayGridMonth',
                                dayMaxEvents: 2,
                                showNonCurrentDates: false,
                                fixedWeekCount: false
                            }
                        }}
                        eventContent={(eventInfo: EventContentArg) =>
                            renderEventContent(eventInfo, CalendarView.OneMonth)
                        }
                        select={handleSelect}
                        firstDay={1}
                        events={events}
                        dayCellClassNames={styles.Cell}
                        dayHeaderClassNames={`${styles.HeaderCellDaycare}`}
                        locales={allLocales}
                        locale={getLocalFromLanguage()}
                        hiddenDays={hiddenDays}
                        eventClick={(e: EventClickArg) => handleEventClick(e)}
                        ref={calendarRef}
                        moreLinkClassNames={`${styles.MoreLinkDaycare}`}
                        noEventsClassNames={`${styles.NoEventsDaycare}`}
                        displayEventTime={false}
                        allDayClassNames={styles.AllDay}
                    />
                </div>
            </div>
            {isDatePickerDialogOpen && (
                <DatePickerDialog
                    open={isDatePickerDialogOpen}
                    handleClose={() => setDatePickerDialogOpen(false)}
                    selectedDate={selectedDate}
                    handleDateChange={setSelectedDate}
                    currentDaycare={currentDaycare}
                />
            )}
        </div>
    )
}

export default ChildPlanning
